import { defineStore } from 'pinia'
import useApi from '@/api/live'
import { getLoginInfo } from '@/utils/CryptoJS.ts'
import { useLocalStorage, useSessionStorage } from '@/hooks/web/useStorage.ts'
import { isWxBrowser } from '@/utils/is.ts'
import { useRouter } from 'vue-router'
import { useAppStore } from '@/store/modules/app.ts'
import { store } from '@/store'

const appStore = useAppStore(store)
const router = useRouter()
const sessionStorage = useSessionStorage()
const localStorage = useLocalStorage()

interface IUserExtraInfo {
  tokenPast: boolean // token是否过期
  blocked?: boolean // 是否被拉黑 1=是 0=否
  muted?: boolean // 是否被禁言 1=是 0=否
  limitVisit?: boolean // 是否被限制访问 1=是 0=否
}

interface IwxConfig {
  appId: string // 小程序appId
  componentAppId: string // 第三方平台appId
}

export const useAuthStore = defineStore('auth', {
  state: () => {
    return {
      wxCode: '',
      paramData: '',
      // 微信配置
      wxConfig: {} as IwxConfig,
      // 用户扩展信息
      userExtraInfo: {} as IUserExtraInfo,
      // 限制观看状态
      limitWatchStatus: false
    }
  },
  getters: {
    // token没过期，并且localStore有token 说明已登录
    isLogin(): boolean {
      return !this.userExtraInfo.tokenPast && getLoginInfo('token')
    },
    // 是否需要重新授权登陆
    isReLogin(): boolean {
      if (this.userExtraInfo.tokenPast) {
        // token 过期
        return true
      }
      if (!getLoginInfo('token')) {
        // 没有登陆信息
        return true
      }
      const oldAuthType = localStorage.get(`auth-type-${sessionStorage.get('roomId')}`)?.toString()
      const newAuthType = appStore.config?.authType + (sessionStorage.get('checkCode') || '')
      // 登陆方式改变 重新登陆
      if (newAuthType && oldAuthType !== newAuthType) {
        // 只有旧登录方式为3，新登录方式为0时，不重新登陆
        return !(oldAuthType === 3 && newAuthType === 0)
      }
      return false
    }
  },
  actions: {
    /**
     * 获取AppId
     */
    async getWxAppid(paramsId: string) {
      try {
        const res: any = await useApi.getAppid(paramsId)
        if (res) {
          this.wxConfig = { appId: res.data.appid, componentAppId: res.data.componentAppId }
        }
      } catch (error) {
        console.error('获取appid失败:', error)
        return null
      }
    },
    // 验证token
    async checkToken(roomId: string) {
      const token = getLoginInfo('token')
      // const name = getLoginInfo("name");
      if (!token) {
        this.userExtraInfo = { tokenPast: true }
      } else {
        const res = await useApi.getUserExtraInfo(roomId)
        const result = res?.code === 200 ? res.data.valid : false
        this.userExtraInfo = {
          tokenPast: !result,
          blocked: res.data?.blocked,
          muted: res?.data?.muted,
          limitVisit: res?.data?.limitVisit
        }
      }
    },
    async userLogin(wxCode: string, userId: string) {
      const userInfo: any = {
        clientId: sessionStorage.get('clientId'),
        roomId: sessionStorage.get('roomId'),
        userId: sessionStorage.get('userId') || userId,
        code: isWxBrowser() ? wxCode : '',
        uuid: sessionStorage.get('autoLoginUid')
      }
      return await useApi.userLogin(userInfo)
    },
    async paramsLogin(wxCode: string, paramData: string) {
      try {
        // const queryData = getQueryParam('data')
        if (!paramData) {
          this.paramsLoginErrorHandler()
        }
        const res = await useApi.paramsLogin({
          code: wxCode,
          clientId: sessionStorage.get('clientId'),
          roomId: sessionStorage.get('roomId'),
          userId: sessionStorage.get('userId'),
          param: encodeURIComponent(paramData)?.replace(/%20/gi, '%2B')
        })
        if (res.code !== 200) {
          this.paramsLoginErrorHandler()
        }
        return res
      } catch (err) {
        console.error(err)
        this.paramsLoginErrorHandler()
      }
    },
    async userLoginByBuyRoomIdApi(wxCode: string) {
      return await useApi.userLoginByBuyRoomIdApi({
        code: wxCode,
        roomId: sessionStorage.get('roomId'),
        clientId: sessionStorage.get('clientId')
      })
    },
    // 白名单无密码登录校验
    async userLoginNoAuth(wxCode: string, userId: string) {
      return await useApi.userLoginNoAuth({
        code: wxCode,
        roomId: sessionStorage.get('roomId'),
        clientId: sessionStorage.get('clientId'),
        userId: sessionStorage.get('userId') || userId
      })
    },
    /**
     * 参数登陆错误处理
     */
    paramsLoginErrorHandler() {
      const tUrl: any = appStore.config?.thirdAuthUrl
      if (tUrl) {
        window.location.href = tUrl
      } else {
        router.replace({ path: '/nopremission/2' }).then()
      }
    }
  }
})
